import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Trophy, AlertTriangle, XCircle, Info, Clock, BarChart2, FileText, ChevronDown, ChevronUp, CheckCircle, Eye } from 'lucide-react';
import './PublicExamStyles.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicExamResultsPage() {
  const { slug, attemptId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReview, setShowReview] = useState(false);

  const [aiGradingInProgress, setAiGradingInProgress] = useState(false);

  useEffect(() => {
    let retryTimer = null;
    
    const fetchResults = async (isRetry = false) => {
      try {
        if (!isRetry) setLoading(true);
        const res = await fetch(`${API_URL}/api/exams/public/attempt/${attemptId}/results`);
        if (!res.ok) {
          throw new Error('Erreur lors de la récupération des résultats');
        }
        const data = await res.json();
        
        // If AI grading is still in progress, show message and retry
        if (data.ai_grading_in_progress) {
          setAiGradingInProgress(true);
          setResult(null);
          setLoading(false);
          retryTimer = setTimeout(() => fetchResults(true), 3000);
          return;
        }
        
        setAiGradingInProgress(false);
        setResult(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (attemptId) {
      fetchResults();
    }
    
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [attemptId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (aiGradingInProgress) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f3f4f6] p-4">
        <div className="bg-white p-10 rounded-2xl shadow-sm text-center max-w-md w-full border border-slate-200">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#f3e8ff] flex items-center justify-center mb-6">
            <Loader2 className="h-8 w-8 animate-spin text-[#8b5cf6]" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-3">Correction en cours...</h2>
          <p className="text-slate-500 text-sm leading-relaxed">
            Votre examen est en cours de correction automatique par l'IA. 
            Veuillez patienter quelques instants, les résultats s'afficheront automatiquement.
          </p>
          <div className="mt-6 flex justify-center">
            <div className="flex gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#8b5cf6] animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="w-2 h-2 rounded-full bg-[#8b5cf6] animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="w-2 h-2 rounded-full bg-[#8b5cf6] animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-sm text-center max-w-sm w-full">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Erreur</h2>
          <p className="text-slate-600">{error || 'Résultats non trouvés'}</p>
        </div>
      </div>
    );
  }

  // Results hidden by instructor — show pending correction template
  if (result.results_hidden) {
    const hiddenDate = result.submitted_at 
      ? new Date(result.submitted_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : null;

    return (
      <div className="min-h-screen bg-[#f3f4f6] p-4 md:p-8 flex items-center justify-center font-sans tracking-tight">
        <div className="w-full max-w-[560px] mx-auto space-y-5">
          {/* Main Card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Header gradient */}
            <div className="h-2 bg-gradient-to-r from-[#8b5cf6] via-[#a78bfa] to-[#c4b5fd]" />
            
            <div className="p-8 text-center">
              {/* Icon */}
              <div className="mx-auto w-20 h-20 rounded-full bg-[#f3e8ff] flex items-center justify-center mb-6">
                <Clock className="h-10 w-10 text-[#8b5cf6]" />
              </div>

              {/* Title */}
              <h1 className="text-2xl font-bold text-slate-800 mb-2">Examen soumis avec succès</h1>
              <p className="text-slate-500 text-sm mb-6">Votre copie est en cours de correction</p>

              {/* Exam info */}
              <div className="bg-[#f8fafc] rounded-xl border border-slate-100 p-5 mb-6 text-left space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Examen</span>
                  <span className="text-sm font-semibold text-slate-800">{result.exam_title}</span>
                </div>
                <div className="border-t border-slate-100" />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Candidat</span>
                  <span className="text-sm font-semibold text-slate-800">{result.candidate_name}</span>
                </div>
                {hiddenDate && (
                  <>
                    <div className="border-t border-slate-100" />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">Soumis le</span>
                      <span className="text-sm font-semibold text-slate-800">{hiddenDate}</span>
                    </div>
                  </>
                )}
                <div className="border-t border-slate-100" />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Questions</span>
                  <span className="text-sm font-semibold text-slate-800">{result.total_questions}</span>
                </div>
              </div>

              {/* Info message */}
              <div className="bg-[#fffbeb] border border-[#fde68a] rounded-xl p-4 flex items-start gap-3 text-left">
                <Info className="w-5 h-5 text-[#d97706] mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#92400e] text-sm">Résultats non encore disponibles</h4>
                  <p className="text-[13px] text-[#b45309] mt-1 leading-relaxed">
                    Votre formateur n'a pas encore rendu les résultats disponibles. Ils seront accessibles une fois la correction terminée. Vous serez informé lorsque vos résultats seront publiés.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-slate-400">
            Conservez le lien de cette page pour consulter vos résultats ultérieurement.
          </p>
        </div>
      </div>
    );
  }

  // Formatting date
  const submittedDate = result.submitted_at 
    ? new Date(result.submitted_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'N/A';

  const questions = result.questions || [];
  const hasQuestions = questions.length > 0;

  return (
    <div className="min-h-screen bg-[#f3f4f6] p-4 md:p-8 flex items-center justify-center font-sans tracking-tight">
      <div className="w-full max-w-[800px] mx-auto space-y-4">
        
        {/* Main Success/Result Banner */}
        <div className={`p-8 rounded-2xl text-center border shadow-sm ${result.passed ? 'bg-[#ecfdf3] border-[#a7f3d0]' : 'bg-[#fef2f2] border-[#fecaca]'}`}>
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-white/50">
            {result.passed ? (
              <Trophy className="h-8 w-8 text-[#10b981]" />
            ) : (
              <XCircle className="h-8 w-8 text-[#ef4444]" />
            )}
          </div>
          
          <div className="inline-block px-4 py-1.5 rounded-lg text-sm font-semibold mb-6 flex items-center justify-center mx-auto w-max shadow-sm"
               style={{ backgroundColor: result.passed ? '#10b981' : '#ef4444', color: 'white' }}>
            {result.passed ? 'Examen réussi !' : 'Examen échoué'}
          </div>

          <h1 className="text-6xl font-bold text-[#111827] mb-2">{typeof result.score === 'number' ? result.score.toFixed(1) : result.score}%</h1>
          <p className="text-[#6b7280] text-sm mb-6">Score minimum requis : {result.passing_score}%</p>
          
          <h2 className="text-2xl font-bold text-[#111827] mb-2">{result.exam_title}</h2>
          <p className="text-[#6b7280] text-sm flex items-center justify-center gap-1.5">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
            Candidat : {result.candidate_name}
          </p>
        </div>

        {/* 3 Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-6 border border-slate-100 flex flex-row items-center gap-4 shadow-sm h-full">
            <div className="w-12 h-12 rounded-xl bg-[#f3e8ff] flex items-center justify-center flex-shrink-0">
               <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" /></svg>
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium mb-1">Score obtenu</p>
              <p className="text-2xl font-bold text-slate-900">{typeof result.points_earned === 'number' ? result.points_earned.toFixed(1) : result.points_earned} / {result.total_points}</p>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 border border-slate-100 flex flex-row items-center gap-4 shadow-sm h-full">
            <div className="w-12 h-12 rounded-xl bg-[#dbeafe] flex items-center justify-center flex-shrink-0">
               <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M16 13H8" /><path d="M16 17H8" /><path d="M10 9H8" /></svg>
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium mb-1">Questions</p>
              <p className="text-2xl font-bold text-slate-900">{result.correct_answers} / {result.total_questions}</p>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 border border-slate-100 flex flex-row items-center gap-4 shadow-sm h-full">
            <div className="w-12 h-12 rounded-xl bg-[#ffedd5] flex items-center justify-center flex-shrink-0">
               <Clock className="w-6 h-6 text-[#f97316]" strokeWidth={2} />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-medium mb-1">Soumis le</p>
              <p className="text-lg font-bold text-slate-900 leading-tight">{submittedDate}</p>
            </div>
          </div>
        </div>

        {/* Manual action required note */}
        {result.manual_grading_count > 0 && (
          <div className="bg-[#fffbeb] border border-[#fde68a] rounded-2xl p-5 flex items-start gap-4 mt-4 shadow-sm">
            <Info className="w-6 h-6 text-[#d97706] mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-[#92400e] text-[15px]">Évaluation en cours</h4>
              <p className="text-[14px] text-[#b45309] mt-1.5 leading-relaxed">
                {result.manual_grading_count} question{result.manual_grading_count > 1 ? 's' : ''} demande{result.manual_grading_count > 1 ? 'nt' : ''} une correction manuelle. Votre formateur l{result.manual_grading_count > 1 ? 'es' : 'a'} corrigera prochainement, et votre score définitif sera alors complété.
              </p>
            </div>
          </div>
        )}

        {/* Review Questions Button */}
        {hasQuestions && (
          <button
            onClick={() => setShowReview(!showReview)}
            className="w-full bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex items-center justify-between hover:border-[#8b5cf6] hover:shadow-md transition-all duration-200 group cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#f3e8ff] flex items-center justify-center flex-shrink-0 group-hover:bg-[#ede9fe] transition-colors">
                <Eye className="w-5 h-5 text-[#8b5cf6]" />
              </div>
              <div className="text-left">
                <h4 className="font-semibold text-slate-800 text-[15px]">Voir le détail des réponses</h4>
                <p className="text-[13px] text-slate-500">Consultez chaque question avec votre réponse et la correction</p>
              </div>
            </div>
            {showReview ? (
              <ChevronUp className="w-5 h-5 text-slate-400 group-hover:text-[#8b5cf6] transition-colors" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400 group-hover:text-[#8b5cf6] transition-colors" />
            )}
          </button>
        )}

        {/* Questions Review List */}
        {showReview && hasQuestions && (
          <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
            {questions.map((q, index) => {
              const isCorrect = q.is_correct;
              const earned = q.points_earned ?? 0;
              const maxPts = q.points ?? 1;

              return (
                <div
                  key={q.id || index}
                  className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${
                    isCorrect ? 'border-[#a7f3d0]' : 'border-[#fecaca]'
                  }`}
                >
                  {/* Question Header */}
                  <div className={`px-5 py-4 flex items-center gap-3 ${
                    isCorrect ? 'bg-[#ecfdf3]' : 'bg-[#fef2f2]'
                  }`}>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isCorrect ? 'bg-[#10b981]/15' : 'bg-[#ef4444]/15'
                    }`}>
                      {isCorrect ? (
                        <CheckCircle className="w-5 h-5 text-[#10b981]" />
                      ) : (
                        <XCircle className="w-5 h-5 text-[#ef4444]" />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-slate-800 text-[15px]">
                        Question {index + 1}
                      </span>
                    </div>
                    <span className={`text-sm font-bold px-3 py-1 rounded-lg ${
                      isCorrect 
                        ? 'bg-[#10b981]/10 text-[#059669]' 
                        : earned > 0 
                          ? 'bg-[#f59e0b]/10 text-[#d97706]'
                          : 'bg-[#ef4444]/10 text-[#dc2626]'
                    }`}>
                      {typeof earned === 'number' ? earned.toFixed(1) : earned} / {maxPts} pts
                    </span>
                  </div>

                  {/* Question Content */}
                  <div className="px-5 py-4 space-y-3">
                    {/* Question Text */}
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Énoncé</p>
                      <div 
                        className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: q.question_text }}
                      />
                    </div>

                    {/* User Answer */}
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Votre réponse</p>
                      <div className={`p-3 rounded-xl text-sm border ${
                        isCorrect 
                          ? 'bg-[#ecfdf3] border-[#a7f3d0] text-[#065f46]'
                          : 'bg-[#fef2f2] border-[#fecaca] text-[#991b1b]'
                      }`}>
                        {q.user_answer || <span className="italic text-slate-400">Pas de réponse</span>}
                      </div>
                    </div>

                    {/* Correct Answer (only show if incorrect) */}
                    {!isCorrect && q.correct_answer && (
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Réponse correcte</p>
                        <div className="p-3 rounded-xl text-sm bg-[#ecfdf3] border border-[#a7f3d0] text-[#065f46]">
                          {q.correct_answer}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
